import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  Navbar,
  NavbarBrand,
  UncontrolledCollapse,
} from 'reactstrap';

import { switchSpecialty } from 'redux/Specialty/SpecialtyAction';
import { getPlansForSpecialty } from 'config/subscriptionPlans';
import { SPECIALTIES } from 'config/specialtyConfig';
import { FELLOWSHIP_CONTENT } from './fellowshipContent';

// Reuse the same CSS classes from the PMR landing page
import 'assets/css/pmr-landing.css';
import 'assets/css/fellowship-landing.css';

const FellowshipLanding = ({ specialtyId }) => {
  const dispatch = useDispatch();
  const content = FELLOWSHIP_CONTENT[specialtyId];
  const plans = getPlansForSpecialty(specialtyId);
  const specialtyName = (SPECIALTIES[specialtyId] || {}).name || 'Board';

  const [isVisible, setIsVisible] = useState({});
  const [scrollY, setScrollY] = useState(0);
  const [navbarScrolled, setNavbarScrolled] = useState(false);

  // Scroll to top and set active specialty on mount / specialty change
  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(switchSpecialty(specialtyId));
  }, [dispatch, specialtyId]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Parallax scroll effect and navbar styling
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setNavbarScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!content) {
    return <div>Fellowship not found.</div>;
  }

  const { colors } = content;

  // CSS custom properties for theming
  const themeVars = {
    '--pmr-secondary': colors.secondary,
    '--pmr-gradient': colors.gradient,
    '--pmr-gradient-light': colors.gradientLight,
  };

  // Build pricing cards from plans config
  const topPlans = plans.slice(0, 4);
  const bottomPlans = plans.slice(4, 8);

  const renderPricingCard = (plan, index, isFeatured = false) => {
    const standardPrice = plan.price;
    const pricePerMonth = (standardPrice / plan.months).toFixed(2);
    const pricingId = `pricing-${index + 1}`;

    return (
      <Col xs="6" md="3" className="mb-4" key={plan.id}>
        <div
          id={pricingId}
          className={`pmr-pricing-card ${isFeatured ? 'pmr-pricing-featured' : ''} animate-on-scroll ${
            isVisible[pricingId] ? 'animate-fade-in-up' : 'opacity-0'
          }`}
          style={{ animationDelay: `${index * 0.05}s` }}
        >
          {isFeatured && (
            <div className="pmr-pricing-badge">
              {plan.months === 48 ? 'BEST Value' : 'Most Popular'}
            </div>
          )}
          <div className="pmr-pricing-header">
            <h3 className="pmr-pricing-name">{plan.months} Month{plan.months > 1 ? 's' : ''}</h3>
            <div className="pmr-pricing-price">
              <span className="pmr-price-amount">${standardPrice}</span>
            </div>
            {plan.months > 1 && (
              <p style={{ color: '#8898aa', fontSize: '0.75rem', margin: '4px 0 0' }}>
                ${pricePerMonth}/mo
              </p>
            )}
          </div>
          <Link
            className={`btn ${isFeatured && plan.popular ? 'pmr-btn-primary' : 'pmr-btn-outline-dark'} btn-sm w-100 mt-3`}
            to="/auth/register"
          >
            Get Started
          </Link>
        </div>
      </Col>
    );
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{content.seo.title}</title>
        <meta name="description" content={content.seo.description} />
        <meta name="keywords" content={content.seo.keywords} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="PM&R Prep" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={content.seo.url} />
        <meta property="og:title" content={content.seo.title} />
        <meta property="og:description" content={content.seo.description} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content.seo.title} />
        <meta name="twitter:description" content={content.seo.description} />
        <link rel="canonical" href={content.seo.url} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Course',
            name: content.seo.title,
            description: content.seo.description,
            url: content.seo.url,
            provider: {
              '@type': 'Organization',
              name: 'PM&R Prep',
              url: 'https://pmrprep.io',
            },
            offers: {
              '@type': 'Offer',
              price: String(plans[0]?.price || '99'),
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              url: 'https://pmrprep.io/auth/register',
            },
          })}
        </script>
      </Helmet>

      <div className="pmr-landing" data-specialty={specialtyId} style={themeVars}>
        {/* Navigation */}
        <Navbar
          className={`pmr-navbar navbar-horizontal navbar-dark fixed-top ${navbarScrolled ? 'scrolled' : ''}`}
          expand="lg"
          style={{
            background: navbarScrolled ? colors.navScrolled + ' !important' : colors.navGradient + ' !important',
          }}
        >
          <Container>
            <NavbarBrand to="/" tag={Link} className="pmr-brand">
              <img
                alt="PM&R Prep Logo"
                src={require('assets/img/brand/pmr-logo.png')}
                className="pmr-nav-logo"
              />
            </NavbarBrand>
            <button className="navbar-toggler" id={`fellowship-navbar-${specialtyId}`}>
              <span className="navbar-toggler-icon" />
            </button>
            <UncontrolledCollapse navbar toggler={`#fellowship-navbar-${specialtyId}`}>
              <Nav className="ml-auto align-items-center" navbar>
                <NavItem>
                  <NavLink href="#features" className="pmr-nav-link">Features</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#topics" className="pmr-nav-link">Topics</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#testimonials" className="pmr-nav-link">Testimonials</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#pricing" className="pmr-nav-link">Pricing</NavLink>
                </NavItem>
                <NavItem className="ml-lg-3">
                  <Link to={`/free-anki-deck/${specialtyId}`} className="btn btn-sm pmr-btn-primary">
                    <i className="fas fa-download mr-1" />
                    Free Anki Deck
                  </Link>
                </NavItem>
                <NavItem className="ml-lg-2">
                  <Link to="/auth/login" className="btn btn-sm pmr-btn-outline">Login</Link>
                </NavItem>
                <NavItem className="ml-lg-2">
                  <Link className="btn btn-sm pmr-btn-primary" to="/auth/register">Start Free Trial</Link>
                </NavItem>
              </Nav>
            </UncontrolledCollapse>
          </Container>
        </Navbar>

        {/* Hero Section */}
        <section className="pmr-hero">
          <div
            className="pmr-hero-bg"
            style={{ transform: `translateY(${scrollY * 0.5}px)` }}
          />
          <div className="pmr-hero-overlay" />
          <Container className="pmr-hero-content">
            <Row className="align-items-center min-vh-100">
              <Col lg="6" className="text-center text-lg-left">
                <div className="pmr-hero-text animate-fade-in-up">
                  <span className="pmr-badge">
                    <i className="fas fa-star mr-2" />
                    {content.badge}
                  </span>
                  <h1 className="pmr-hero-title">
                    {content.heroTitle[0]}
                    <span className="pmr-text-gradient">{content.heroTitle[1]}</span>
                    {content.heroTitle[2]}
                  </h1>
                  <p className="pmr-hero-subtitle">{content.heroSubtitle}</p>
                  <div className="pmr-hero-cta">
                    <Link className="btn pmr-btn-primary pmr-btn-lg mr-3" to="/auth/register">
                      Start Free Trial
                      <i className="fas fa-arrow-right ml-2" />
                    </Link>
                    <a className="btn pmr-btn-secondary pmr-btn-lg" href="#features">
                      Learn More
                    </a>
                  </div>
                  {content.trustText && (
                    <div className="pmr-hero-trust mt-4">
                      <span className="pmr-trust-text">
                        <i className="fas fa-shield-alt mr-2" />
                        {content.trustText}
                      </span>
                    </div>
                  )}
                </div>
              </Col>
              <Col lg="6" className="d-none d-lg-block">
                <div className="pmr-hero-image animate-fade-in-right">
                  <div className="pmr-floating-card pmr-card-1 animate-float">
                    <i className="fas fa-check-circle mr-2" style={{ color: content.colors.secondary }} />
                    <span>Question Completed!</span>
                  </div>
                  <div className="pmr-floating-card pmr-card-2 animate-float-delayed">
                    <i className="fas fa-chart-line text-info mr-2" />
                    <span>Score: 92%</span>
                  </div>
                  <img
                    src={require('assets/img/brand/pmr-logo.png')}
                    alt={`${content.heroTitle[1]} Dashboard`}
                    className="pmr-hero-logo-main"
                  />
                </div>
              </Col>
            </Row>
          </Container>
          <div className="pmr-hero-wave">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
                fill="#ffffff"
              />
            </svg>
          </div>
        </section>

        {/* Free Anki Deck Lead Magnet */}
        <section className="pmr-group-pricing" id="free-anki-deck">
          <Container>
            <Row className="align-items-center">
              <Col lg="6" className="mb-5 mb-lg-0">
                <div className="animate-on-scroll opacity-0 animate-fade-in-up">
                  <span className="pmr-group-badge">FREE DOWNLOAD</span>
                  <h2 className="pmr-section-title" style={{ marginTop: '1rem' }}>
                    Get Our {specialtyName}{' '}
                    <span className="pmr-text-gradient">Anki Deck Free</span>
                  </h2>
                  <p style={{ fontSize: '1.125rem', color: '#8898aa', lineHeight: 1.7, marginBottom: '2rem' }}>
                    High-yield flashcards covering the {specialtyName} board content.
                    No trial required — just tell us where to send it.
                  </p>
                  <div className="pmr-group-benefits">
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Built for the {specialtyName} boards</span>
                    </div>
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Works in the free Anki app</span>
                    </div>
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Study on desktop or mobile</span>
                    </div>
                  </div>
                  <Link
                    to={`/free-anki-deck/${specialtyId}`}
                    className="btn pmr-btn-primary pmr-btn-lg"
                    style={{ marginTop: '2rem' }}
                  >
                    Claim the Free Deck <i className="fas fa-arrow-right ml-2" />
                  </Link>
                </div>
              </Col>
              <Col lg="6">
                <div className="animate-on-scroll opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  <div className="pmr-group-card">
                    <div className="pmr-group-card-icon">
                      <i className="fas fa-layer-group" />
                    </div>
                    <h3 className="pmr-group-card-title">{specialtyName} Anki Deck</h3>
                    <p className="pmr-group-card-subtitle">Spaced repetition, done right</p>
                    <div className="pmr-group-card-highlight">
                      <i className="fas fa-gift mr-2" />
                      Completely Free
                    </div>
                    <ul className="pmr-group-card-features">
                      <li><i className="fas fa-check mr-2" />Mapped to the board content outline</li>
                      <li><i className="fas fa-check mr-2" />Commit facts to long-term memory</li>
                      <li><i className="fas fa-check mr-2" />Study on desktop or mobile</li>
                      <li><i className="fas fa-check mr-2" />Updated as the deck grows</li>
                    </ul>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Group Pricing Section */}
        <section className="pmr-group-pricing" id="group-pricing">
          <Container>
            <Row className="align-items-center">
              <Col lg="6" className="mb-5 mb-lg-0">
                <div className="animate-on-scroll opacity-0 animate-fade-in-up">
                  <span className="pmr-group-badge">{content.groupCard.badgeText}</span>
                  <h2 className="pmr-section-title" style={{ marginTop: '1rem' }}>
                    Equip Your Entire Program &{' '}
                    <span className="pmr-text-gradient">Save Big</span>
                  </h2>
                  <p style={{ fontSize: '1.125rem', color: '#8898aa', lineHeight: 1.7, marginBottom: '2rem' }}>
                    Get exclusive group rates when you purchase 5+ seats.
                    Dedicated coordinator tools included.
                  </p>
                  <div className="pmr-group-benefits">
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Significant discount on individual pricing</span>
                    </div>
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Easy code distribution to fellows</span>
                    </div>
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Bulk access management</span>
                    </div>
                  </div>
                  <Link
                    to="/group-pricing"
                    className="btn pmr-btn-primary pmr-btn-lg"
                    style={{ marginTop: '2rem' }}
                  >
                    Get Group Pricing <i className="fas fa-arrow-right ml-2" />
                  </Link>
                </div>
              </Col>
              <Col lg="6">
                <div className="animate-on-scroll opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  <div className="pmr-group-card">
                    <div className="pmr-group-card-icon">
                      <i className="fas fa-hospital" />
                    </div>
                    <h3 className="pmr-group-card-title">{content.groupCard.title}</h3>
                    <p className="pmr-group-card-subtitle">{content.groupCard.subtitle}</p>
                    <div className="pmr-group-card-seats">
                      <i className="fas fa-users mr-2" />
                      5 &ndash; 50+ seats
                    </div>
                    <div className="pmr-group-card-highlight">
                      <i className="fas fa-tag mr-2" />
                      Exclusive Group Discount
                    </div>
                    <ul className="pmr-group-card-features">
                      {content.groupCard.features.map((feature, i) => (
                        <li key={i}><i className="fas fa-check mr-2" />{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Features Section */}
        <section id="features" className="pmr-features">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title">
                  Everything You Need to <span className="pmr-text-gradient">Pass Your Boards</span>
                </h2>
                <p className="pmr-section-subtitle">
                  Our comprehensive platform provides all the tools you need for effective board preparation.
                </p>
              </Col>
            </Row>
            <Row>
              {content.features.map((feature, index) => (
                <Col key={index} md="6" lg="4" className="mb-4">
                  <div
                    id={`feature-${index}`}
                    className={`pmr-feature-card animate-on-scroll ${
                      isVisible[`feature-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div
                      className="pmr-feature-icon"
                      style={{ backgroundColor: `${feature.color}20`, color: feature.color }}
                    >
                      <i className={feature.icon} />
                    </div>
                    <h3 className="pmr-feature-title">{feature.title}</h3>
                    <p className="pmr-feature-description">{feature.description}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Stats Section */}
        <section className="pmr-stats">
          <Container>
            <Row className="justify-content-center">
              {content.stats.map((stat, index) => (
                <Col key={index} xs="6" md="3" className="text-center mb-4 mb-md-0">
                  <div
                    id={`stat-${index}`}
                    className={`pmr-stat-item animate-on-scroll ${
                      isVisible[`stat-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <p className="pmr-stat-number">{stat.number}</p>
                    <p className="pmr-stat-label">{stat.label}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Topics Section */}
        <section id="topics" className="pmr-topics">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title text-white">
                  Comprehensive <span className="pmr-text-gradient-light">Topic Coverage</span>
                </h2>
                <p className="pmr-section-subtitle text-white-80">
                  Our question bank covers all major topics aligned with the board content outline.
                </p>
              </Col>
            </Row>
            <Row className="justify-content-center">
              {content.topics.map((topic, index) => (
                <Col key={index} xs="6" md="4" lg="3" className="mb-3">
                  <div
                    id={`topic-${index}`}
                    className={`pmr-topic-tag animate-on-scroll ${
                      isVisible[`topic-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <i className="fas fa-check-circle mr-2" />
                    {topic}
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* How It Works Section */}
        <section className="pmr-how-it-works">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title">
                  How <span className="pmr-text-gradient">It</span> Works
                </h2>
                <p className="pmr-section-subtitle">
                  Get started in minutes and begin your journey to board certification.
                </p>
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col md="4" className="text-center mb-4 mb-md-0">
                <div
                  id="step-1"
                  className={`pmr-step animate-on-scroll ${
                    isVisible['step-1'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                >
                  <div className="pmr-step-number">1</div>
                  <h4 className="pmr-step-title">Create Account</h4>
                  <p className="pmr-step-description">
                    Sign up for free and get instant access to sample questions.
                  </p>
                </div>
              </Col>
              <Col md="4" className="text-center mb-4 mb-md-0">
                <div
                  id="step-2"
                  className={`pmr-step animate-on-scroll ${
                    isVisible['step-2'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: '0.2s' }}
                >
                  <div className="pmr-step-number">2</div>
                  <h4 className="pmr-step-title">Practice Questions</h4>
                  <p className="pmr-step-description">
                    Study with our board-style questions and detailed explanations.
                  </p>
                </div>
              </Col>
              <Col md="4" className="text-center">
                <div
                  id="step-3"
                  className={`pmr-step animate-on-scroll ${
                    isVisible['step-3'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: '0.4s' }}
                >
                  <div className="pmr-step-number">3</div>
                  <h4 className="pmr-step-title">Pass Your Boards</h4>
                  <p className="pmr-step-description">
                    Track your progress and walk into your exam with confidence.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="pmr-testimonials">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title">
                  What Our <span className="pmr-text-gradient">Users Say</span>
                </h2>
                <p className="pmr-section-subtitle">
                  Join physicians who passed their boards with PM&R Prep.
                </p>
              </Col>
            </Row>
            <Row>
              {content.testimonials.map((testimonial, index) => (
                <Col key={index} md="4" className="mb-4">
                  <div
                    id={`testimonial-${index}`}
                    className={`pmr-testimonial-card animate-on-scroll ${
                      isVisible[`testimonial-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="pmr-testimonial-stars">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="fas fa-star" />
                      ))}
                    </div>
                    <p className="pmr-testimonial-text">"{testimonial.text}"</p>
                    <div className="pmr-testimonial-author">
                      <div className="pmr-testimonial-avatar">{testimonial.avatar}</div>
                      <div>
                        <h5 className="pmr-testimonial-name">{testimonial.name}</h5>
                        <p className="pmr-testimonial-role">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="pmr-pricing">
          <Container>
            <Row className="justify-content-center mb-4">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title text-white" style={{ marginBottom: '8px' }}>
                  Simple <span className="pmr-text-gradient-light">Pricing</span>
                </h2>
                <p className="pmr-section-subtitle text-white-80" style={{ marginBottom: '10px' }}>
                  Start with a 14-day free trial. You won't be charged until your trial ends.
                </p>
              </Col>
            </Row>

            {/* Top row: 1mo, 3mo, 6mo, 12mo */}
            <Row className="justify-content-center">
              {topPlans.map((plan, index) => {
                const isFeatured = plan.popular;
                return renderPricingCard(plan, index, isFeatured);
              })}
            </Row>

            {/* Bottom row: 18mo, 24mo, 36mo, 48mo */}
            <Row className="justify-content-center">
              {bottomPlans.map((plan, index) => {
                const is48Month = plan.months === 48;
                return renderPricingCard(plan, index + 4, is48Month);
              })}
            </Row>

            {/* All Plans Include */}
            <Row className="justify-content-center mt-4">
              <Col lg="8" className="text-center">
                <div style={{
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  padding: '20px',
                  borderRadius: '10px'
                }}>
                  <h4 style={{ color: '#fff', marginBottom: '15px' }}>All Plans Include:</h4>
                  <Row>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check mr-2" style={{ color: content.colors.secondary }}></i>Full Question Bank
                      </span>
                    </Col>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check mr-2" style={{ color: content.colors.secondary }}></i>{content.stats[0].number} Questions
                      </span>
                    </Col>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check mr-2" style={{ color: content.colors.secondary }}></i>Detailed Explanations
                      </span>
                    </Col>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check mr-2" style={{ color: content.colors.secondary }}></i>Performance Analytics
                      </span>
                    </Col>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check mr-2" style={{ color: content.colors.secondary }}></i>Timed Practice Exams
                      </span>
                    </Col>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check mr-2" style={{ color: content.colors.secondary }}></i>Mobile Access
                      </span>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="pmr-cta">
          <Container>
            <Row className="justify-content-center">
              <Col lg="8" className="text-center">
                <h2 className="pmr-cta-title">{content.ctaTitle}</h2>
                <p className="pmr-cta-subtitle">{content.ctaSubtitle}</p>
                <Link className="btn pmr-btn-white pmr-btn-lg" to="/auth/register">
                  Start Free Trial
                  <i className="fas fa-arrow-right ml-2" />
                </Link>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Footer */}
        <footer className="pmr-footer">
          <Container>
            <Row>
              <Col lg="4" className="mb-4 mb-lg-0">
                <img
                  src={require('assets/img/brand/pmr-logo.png')}
                  alt="PM&R Prep"
                  className="pmr-footer-logo"
                />
                <p className="pmr-footer-description">{content.footerDescription}</p>
                <div className="pmr-social-links">
                  <a href="mailto:support@pmrprep.io" aria-label="Email">
                    <i className="fas fa-envelope" />
                  </a>
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-4 mb-md-0">
                <h5 className="pmr-footer-title">Product</h5>
                <ul className="pmr-footer-links">
                  <li><a href="#features">Features</a></li>
                  <li><a href="#pricing">Pricing</a></li>
                  <li><Link to="/#fellowship">Fellowship</Link></li>
                  <li><a href="#testimonials">Reviews</a></li>
                </ul>
              </Col>
              <Col lg="2" md="4" className="mb-4 mb-md-0">
                <h5 className="pmr-footer-title">Resources</h5>
                <ul className="pmr-footer-links">
                  <li><Link to="/auth/register">Get Started</Link></li>
                  <li><Link to="/auth/login">Sign In</Link></li>
                  <li><Link to="/">PM&R Home</Link></li>
                </ul>
              </Col>
              <Col lg="2" md="4">
                <h5 className="pmr-footer-title">Contact</h5>
                <ul className="pmr-footer-links pmr-footer-contact">
                  <li>
                    <i className="fas fa-envelope mr-2" />
                    support@pmrprep.io
                  </li>
                </ul>
              </Col>
            </Row>
            <hr className="pmr-footer-divider" />
            <Row>
              <Col className="text-center">
                <p className="pmr-copyright">
                  &copy; {new Date().getFullYear()} PM&R Prep. All rights reserved.
                </p>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>
    </>
  );
};

export default FellowshipLanding;
